import { Icon } from "@circle-react-shared/Icon";
import {
  dateFormats,
  formattedDateTime,
} from "@circle-react/helpers/dateTimeHelpers/formattedDateTime";
import { Badge } from "@circleco/compass/components/Badge";
import { Tooltip } from "@circleco/compass/components/Tooltip";

import { t } from "@/i18n-js/instance";
import { TippyV2 } from "@/react/components/SettingsApp/CompassCompat/Tooltip";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

import type { ConnectedBadgeProps } from "./types";

const sizeClasses = {
  sm: "!h-5 !px-2 gap-1",
  md: "!h-8 !px-4 gap-1.5",
  lg: "!h-10 !px-5 gap-2",
  full: "w-full",
};

export const ConnectedBadge = ({
  connectedAt,
  showTooltip = true,
  size = "md",
  className,
}: ConnectedBadgeProps) => {
  const { isCircleV4 } = useCircleV4();

  const tooltipContent = connectedAt
    ? t("members_directory.connected_on_hover_text", {
        date_string: formattedDateTime({
          dateTime: new Date(connectedAt),
          format: dateFormats.short_month_and_date_with_year,
        }),
      })
    : t("profile_drawer.connect_banner.states.active");

  if (isCircleV4) {
    const badge = (
      <Badge
        label={t("profile_drawer.connect_banner.states.active")}
        variant="secondary"
        data-testid="connected-badge"
      />
    );

    if (showTooltip && connectedAt) {
      return (
        <Tooltip content={tooltipContent} side="bottom">
          {badge}
        </Tooltip>
      );
    }

    return badge;
  }

  const badge = (
    <div
      className={classNames(
        "bg-secondary text-dark flex items-center justify-center rounded-3xl font-medium",
        sizeClasses[size],
        className,
      )}
      data-testid="connected-badge"
    >
      <Icon
        type="20-checkmark"
        size={20}
        useWithFillCurrentColor
        className="size-4"
      />
      <Typography.LabelXs weight="medium">
        {t("profile_drawer.connect_banner.states.active")}
      </Typography.LabelXs>
    </div>
  );

  if (showTooltip && connectedAt) {
    return (
      <TippyV2 content={tooltipContent} placement="bottom">
        {badge}
      </TippyV2>
    );
  }

  return badge;
};
